import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "../UI/Button";
import { apiUrl } from "../../config/api";

// ✅ Background image (replace with your own if you want)
const BG_IMAGE =
  "https://images.unsplash.com/photo-1509099836639-18ba1795216d?q=80&w=1920&auto=format&fit=crop";

export default function DonateCheckout() {
  const navigate = useNavigate();

  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("Education Support");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");

  // ✅ qrph | gcash | card
  const [method, setMethod] = useState("qrph");

  // ✅ Card inputs
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvc, setCvc] = useState("");

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [msg, setMsg] = useState("");

  // ✅ QRPH display
  const [qrUrl, setQrUrl] = useState("");
  const [qrRef, setQrRef] = useState("");

  const normalizeCardNumber = (v) => String(v || "").replace(/[^\d]/g, "");
  const normalizeMonth = (v) => String(v || "").replace(/[^\d]/g, "").slice(0, 2);
  const normalizeYear = (v) => String(v || "").replace(/[^\d]/g, "").slice(0, 4);
  const normalizeCvc = (v) => String(v || "").replace(/[^\d]/g, "").slice(0, 4);

  const ensure4DigitYear = (yy) => {
    const y = String(yy || "").trim();
    if (y.length === 2) return `20${y}`; // ✅ convert 30 -> 2030
    return y;
  };

  const createIntent = async () => {
    const res = await fetch(apiUrl("/api/donations/create-intent"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount: Number(amount),
        purpose,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        type: "One-time",
        currency: "PHP",
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || "Create intent failed");
    return data; // { payment_intent_id, client_key, status }
  };

  // ✅ Robust next_action parser (works with your ATTACH RESPONSE shape)
  const pickNextAction = (attachData) => {
    return (
      attachData?.data?.attributes?.next_action ||
      attachData?.attributes?.next_action ||
      attachData?.next_action ||
      null
    );
  };

  const pickRedirectUrl = (nextAction) => {
    return (
      nextAction?.redirect?.url ||
      nextAction?.redirect?.href ||
      nextAction?.redirect_url ||
      null
    );
  };

  const pickQrImage = (nextAction) => {
    // ✅ YOUR LIVE RESPONSE: next_action.code.image_url (base64 PNG)
    return (
      nextAction?.code?.image_url || // ✅ consume_qr
      nextAction?.display_qr?.image_url ||
      nextAction?.qrph?.image_url ||
      nextAction?.qrph?.qr_code_url ||
      nextAction?.qr_code_url ||
      null
    );
  };

  // ✅ Status checker (polls your DB via backend)
const checkStatus = async (intentId, { silent = false } = {}) => {
  if (!intentId) return false;

  if (!silent) setChecking(true);
  try {
    const res = await fetch(
      apiUrl(
        `/api/donations/public-status?payment_intent_id=${encodeURIComponent(intentId)}`
      )
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || "Failed to check status");

    if (data.status === "Completed") {
      setMsg("Payment confirmed ✅ Redirecting...");
      toast.success("Payment confirmed!");
      navigate("/donate-success");
      return true;
    }

    if (data.status === "Failed") {
      setMsg("Payment failed ❌ Please try again.");
      toast.error("Payment failed. Please try again.");
      return true;
    }

    return false;
  } catch (e) {
    if (!silent) {
      setMsg(e.message || "Unable to check status");
      toast.error(e.message || "Unable to check status");
    }
    return false;
  } finally {
    if (!silent) setChecking(false);
  }
};

  // ✅ AUTO-REDIRECT for QRPH after payment (wait for webhook to update DB)
  useEffect(() => {
    if (method !== "qrph") return;
    if (!qrUrl || !qrRef) return;

    let alive = true;
    let tries = 0;

    const MAX_TRIES = 120; // 120 * 3s = 6 minutes (adjust if you want)
    const INTERVAL_MS = 3000;

    const tick = async () => {
      if (!alive) return;

      tries += 1;
      const done = await checkStatus(qrRef, { silent: true });

      if (done) return; // completed/failed
      if (tries >= MAX_TRIES) {
        setMsg("Still waiting for confirmation... Please refresh or check dashboard.");
        return;
      }

      setTimeout(tick, INTERVAL_MS);
    };

    setChecking(true);
    setMsg("Waiting for payment confirmation... (auto-checking)");
    toast("Waiting for payment confirmation...");
    setTimeout(tick, 1500);

    return () => {
      alive = false;
      setChecking(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, qrUrl, qrRef]);

const pay = async () => {
  setMsg("");
  setQrUrl("");
  setQrRef("");
  setLoading(true);

  const toastId = "donate-pay";

  try {
    toast.loading("Preparing payment...", { id: toastId });

    if (!amount || Number(amount) <= 0) throw new Error("Enter a valid amount");
    if (donorEmail && !donorEmail.includes("@")) throw new Error("Enter a valid email");

    const cleanCard = normalizeCardNumber(cardNumber);
    const cleanMonth = normalizeMonth(expMonth);
    const cleanYear = ensure4DigitYear(normalizeYear(expYear));
    const cleanCvc = normalizeCvc(cvc);

    if (method === "card") {
      if (!cleanCard || cleanCard.length < 12) throw new Error("Enter a valid card number");
      if (!cleanMonth || Number(cleanMonth) < 1 || Number(cleanMonth) > 12)
        throw new Error("Enter valid exp month (1-12)");
      if (!cleanYear || String(cleanYear).length !== 4)
        throw new Error("Enter valid exp year (e.g. 2030 or 30)");
      if (!cleanCvc || String(cleanCvc).length < 3) throw new Error("Enter valid CVC");
    }

    if (method === "qrph" && Number(amount) < 1) {
      throw new Error("QRPH minimum is ₱1. Please enter 1 or higher.");
    }
    if (method === "gcash" && Number(amount) < 20) {
      throw new Error("GCash minimum is ₱20. Please enter 20 or higher.");
    }

    const { payment_intent_id } = await createIntent();
    localStorage.setItem("last_payment_intent_id", payment_intent_id);
    setQrRef(payment_intent_id);

    const pmRes = await fetch(apiUrl("/api/donations/create-payment-method"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        method,
        billing: {
          name: donorName || "Anonymous",
          email: donorEmail || "anonymous@example.com",
        },
        card:
          method === "card"
            ? {
                number: cleanCard,
                exp_month: Number(cleanMonth),
                exp_year: Number(cleanYear),
                cvc: cleanCvc,
              }
            : null,
      }),
    });

    const pmData = await pmRes.json().catch(() => ({}));
    if (!pmRes.ok) throw new Error(pmData.message || "Failed to create payment method");

    const attachRes = await fetch(apiUrl("/api/donations/attach-payment-method"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        payment_intent_id,
        payment_method_id: pmData.payment_method_id,
      }),
    });

    const attachData = await attachRes.json().catch(() => ({}));
    if (!attachRes.ok) throw new Error(attachData.message || "Failed to attach payment method");

    const nextAction = pickNextAction(attachData);

    const redirectUrl = pickRedirectUrl(nextAction);
    if (redirectUrl) {
      setMsg("Redirecting to checkout...");
      toast.success("Redirecting to payment page...", { id: toastId });
      window.location.href = redirectUrl;
      return;
    }

    const qr = pickQrImage(nextAction);

    if (method === "qrph") {
      if (!qr) {
        console.log("ATTACH RESPONSE (no qr found):", attachData);
        throw new Error("QR code not returned. Expected next_action.code.image_url from PayMongo.");
      }

      setQrUrl(qr);
      setMsg("Scan the QR using GCash/Maya/Bank app. After paying, wait for webhook confirmation ✅");
      toast.success("QR code generated successfully.", { id: toastId });
      return;
    }

    setMsg("Payment created. Wait for confirmation in dashboard ✅ (webhook)");
    toast.success("Payment created successfully.", { id: toastId });
  } catch (e) {
    setMsg(e.message || "Payment failed");
    toast.error(e.message || "Payment failed", { id: toastId });
  } finally {
    setLoading(false);
  }
};

  // ---------- UI helpers ----------
  const inputBase =
    "w-full rounded-xl border bg-white/95 dark:bg-gray-950/60 px-4 py-2.5 text-gray-900 dark:text-gray-100 " +
    "border-gray-200 dark:border-gray-800 " +
    "placeholder:text-gray-400 dark:placeholder:text-gray-500 " +
    "outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 dark:focus:border-blue-400 transition";

  const labelBase = "text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-200 uppercase";

  const sectionCard =
    "rounded-2xl border border-white/10 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl";

  const chipBase =
    "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition " +
    "focus:outline-none focus:ring-4 focus:ring-blue-500/20";

  const chipActive =
    "border-blue-500/70 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-400/40";

  const chipInactive =
    "border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-200 dark:hover:bg-gray-800/60";

  const showCard = method === "card";

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${BG_IMAGE})` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/75 to-black/85 backdrop-blur-md" />

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-400/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-indigo-400/20 blur-3xl" />
      </div>

      <div className="relative p-5 sm:p-8 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-xl">
          <div className="mb-4 sm:mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-3 py-1 text-xs text-white/95 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Payments • webhook • receipt
            </div>

            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white drop-shadow-xl">
              Donation Checkout
            </h1>
            <p className="mt-1 text-sm text-white/90 drop-shadow-md">
              Choose a payment method then proceed.
            </p>
          </div>

          <div className={`${sectionCard} p-4 sm:p-6`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={labelBase}>Amount (PHP)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                    ₱
                  </span>
                  <input
                    className={`${inputBase} pl-9`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    inputMode="decimal"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={labelBase}>Purpose</label>
                <select className={inputBase} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                  <option>Education Support</option>
                  <option>Healthcare</option>
                  <option>Food & Nutrition</option>
                  <option>Infrastructure</option>
                  <option>General Support</option>
                </select>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={labelBase}>Name (optional)</label>
                <input
                  className={inputBase}
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="Anonymous"
                  autoComplete="name"
                />
              </div>

              <div className="space-y-2">
                <label className={labelBase}>Email (optional)</label>
                <input
                  className={inputBase}
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="email@example.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className={labelBase}>Payment Method</div>
                </div>

                <div className="flex gap-2 flex-wrap justify-end">
                  <button
                    type="button"
                    className={`${chipBase} ${method === "qrph" ? chipActive : chipInactive}`}
                    onClick={() => setMethod("qrph")}
                    aria-pressed={method === "qrph"}
                  >
                    <span className="text-base">🧾</span> QRPH
                  </button>

                  <button
                    type="button"
                    className={`${chipBase} ${method === "gcash" ? chipActive : chipInactive}`}
                    onClick={() => setMethod("gcash")}
                    aria-pressed={method === "gcash"}
                  >
                    <span className="text-base">📱</span> GCash
                  </button>

                  <button
                    type="button"
                    className={`${chipBase} ${method === "card" ? chipActive : chipInactive}`}
                    onClick={() => setMethod("card")}
                    aria-pressed={method === "card"}
                  >
                    <span className="text-base">💳</span> Card
                  </button>
                </div>
              </div>

              {/* CARD DETAILS */}
              <div
                className={`mt-4 overflow-hidden transition-all duration-300 ${
                  showCard ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50/90 dark:bg-gray-950/40 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Card Details</div>
                    </div>
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">Required</div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="space-y-2">
                      <label className={labelBase}>Card Number</label>
                      <input
                        className={inputBase}
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        placeholder="4242 4242 4242 4242"
                        inputMode="numeric"
                        autoComplete="cc-number"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <label className={labelBase}>Exp Month</label>
                        <input
                          className={inputBase}
                          value={expMonth}
                          onChange={(e) => setExpMonth(e.target.value)}
                          placeholder="12"
                          inputMode="numeric"
                          autoComplete="cc-exp-month"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className={labelBase}>Exp Year</label>
                        <input
                          className={inputBase}
                          value={expYear}
                          onChange={(e) => setExpYear(e.target.value)}
                          placeholder="2030"
                          inputMode="numeric"
                          autoComplete="cc-exp-year"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className={labelBase}>CVC</label>
                        <input
                          className={inputBase}
                          value={cvc}
                          onChange={(e) => setCvc(e.target.value)}
                          placeholder="123"
                          inputMode="numeric"
                          autoComplete="cc-csc"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* QR DISPLAY */}
              {method === "qrph" && qrUrl && (
                <div className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/40 p-4">
                  <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Scan to Pay (QRPH)</div>
                  <div className="mt-1 text-xs text-gray-700 dark:text-gray-200">
                    Scan using GCash/Maya/Bank app. After paying, wait for webhook confirmation.
                  </div>

                  <div className="mt-4 flex items-center justify-center">
                    <img
                      src={qrUrl}
                      alt="QRPH Code"
                      className="h-64 w-64 rounded-xl border border-gray-200 dark:border-gray-800 bg-white"
                    />
                  </div>

                  {qrRef && (
                    <div className="mt-3 text-center text-xs text-gray-700 dark:text-gray-200">
                      Reference: <span className="font-mono">{qrRef}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {msg && (
              <div className="mt-5 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/40 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 h-8 w-8 rounded-xl bg-blue-500/10 text-blue-700 dark:text-blue-200 flex items-center justify-center">
                    ℹ️
                  </div>
                  <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                    {msg}{" "}
                    {checking ? <span className="ml-1 text-xs opacity-80">(checking...)</span> : null}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <Button
                variant="primary"
                size="medium"
                disabled={loading || (method === "qrph" && !!qrUrl)}
                onClick={pay}
                className="w-full !rounded-2xl !py-3.5"
              >
                {loading
                  ? "Processing..."
                  : method === "qrph" && qrUrl
                  ? "QR Generated ✅ Waiting..."
                  : method === "gcash"
                  ? "Donate with GCash"
                  : method === "qrph"
                  ? "Generate QR (Scan to Pay)"
                  : "Donate with Card"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}